import React, { useState, useRef, useEffect } from "react";
import colors from "colors/color";
import searchIcon from "../../assets/img/search_icon.svg";
import filterIcon from "../../assets/img/filter_icon.svg";
import locationIcon from "../../assets/img/location_icon.svg";
import {
  Box,
  Button,
  Divider,
  Input,
  Paper,
  styled,
  useMediaQuery,
  useTheme,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Popper,
  ClickAwayListener,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const SearchContainer = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: {
    xs: "column",
    md: "row",
  },
  gap: "8px",
  padding: "8px",
  borderRadius: 20,
  boxShadow: "0px 4px 30px 0px #0000000D",
  height: 68,

  [theme.breakpoints.up("md")]: {
    width: "610px",
    maxWidth: "610px",
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    height: "auto",
    margin: "0 16px",
    width: "100%",
    paddingBottom: 12,
    padding: "17px",
  },
}));

const FilterContainer = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: "16px",
  borderRadius: 12,
  boxShadow: "0px 4px 30px 0px #0000000D",
  width: "180px",
}));

const InputWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "0 16px",
  flex: 1,
  borderLeft: (props) => (props.isSecondInput ? "1px solid #f1f1f1" : "none"),
  [theme.breakpoints.down("md")]: {
    borderLeft: "none",
    borderTop: (props) => (props.isSecondInput ? "1px solid #f1f1f1" : "none"),
    padding: "8px 16px",
  },
}));

const SearchInput = styled(Input)({
  "&.MuiInput-root": {
    border: "none",
    "&:before, &:after": {
      display: "none",
    },
  },
  "& .MuiInput-input": {
    padding: 0,
  },
});

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  "& .MuiTypography-root": {
    fontFamily: "Poppins",
    fontSize: "14px",
  },
  marginLeft: 0,
  marginRight: 0,
}));

function SearchBar({
  searchTerm,
  location,
  handleSearch,
  setLocation,
  setSearchTerm,
  showSnackbar,
  setMode,
  setType,
  mode,
  type,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablette = useMediaQuery(theme.breakpoints.down("md"));
  const apiUrl = process.env.REACT_APP_API_URL;

  // Add refs for autocomplete
  const searchInputRef = useRef(null);
  const locationInputRef = useRef(null);

  // Separate states for filter and autocomplete
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [suggestionAnchorEl, setSuggestionAnchorEl] = useState(null);
  const open = Boolean(filterAnchorEl);

  // Add suggestions state
  const [suggestions, setSuggestions] = useState({
    titles: [],
    locations: [],
  });

  // Filter options state
  const [filters, setFilters] = useState({
    onSite: false,
    remote: false,
    hybrid: false,
    job: false,
    internship: false,
  });

  // Synchronize filter state with passed props
  useEffect(() => {
    setFilters(prevFilters => ({
      ...prevFilters,
      // Set mode filters based on passed mode prop
      onSite: mode === "On site",
      remote: mode === "Remote",
      hybrid: mode === "Hybrid",
      // Set type filters based on passed type prop
      job: type === "j",
      internship: type === "i",
    }));
  }, [mode, type]);

  // Add debounce function
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func.apply(null, args);
      }, delay);
    };
  };

  // Add fetchSuggestions function
  const fetchSuggestions = async (query, type) => {
    if (query.trim().length < 2) {
      setSuggestions({ titles: [], locations: [] });
      return;
    }
    try {
      const url = `${apiUrl}posts/autocomplete/?query=${encodeURIComponent(
        query
      )}&type=${type}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (type === "title") {
        setSuggestions((prev) => ({ ...prev, titles: data.suggestions }));
      } else {
        setSuggestions((prev) => ({ ...prev, locations: data.suggestions }));
      }
    } catch (error) {
      console.error(`Error fetching ${type} suggestions:`, error);
    }
  };

  const debouncedTitleSuggestions = debounce(
    (query) => fetchSuggestions(query, "title"),
    300
  );
  const debouncedLocationSuggestions = debounce(
    (query) => fetchSuggestions(query, "localisation"),
    300
  );

  // Update handlers for autocomplete
  const handleSearchTermChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedTitleSuggestions(value);

    if (value.trim().length >= 2) {
      setSuggestionAnchorEl(searchInputRef.current);
    } else {
      setSuggestionAnchorEl(null);
    }
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setLocation(value);
    debouncedLocationSuggestions(value);

    if (value.trim().length >= 2) {
      setSuggestionAnchorEl(locationInputRef.current);
    } else {
      setSuggestionAnchorEl(null);
    }
  };

  const handleSuggestionSelect = (suggestion, type) => {
    if (type === "title") {
      setSearchTerm(suggestion);
    } else {
      setLocation(suggestion);
    }
    setSuggestionAnchorEl(null);
  };

  // Update filter handlers
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterChange = (event) => {
    const { name, checked } = event.target;

    // Update filter state
    setFilters(prevFilters => {
      const newFilters = { ...prevFilters };
      
      // Handle mode filters (On site, Remote, Hybrid)
      if (["onSite", "remote", "hybrid"].includes(name)) {
        // Uncheck other mode filters
        newFilters.onSite = name === "onSite" && checked;
        newFilters.remote = name === "remote" && checked;
        newFilters.hybrid = name === "hybrid" && checked;
        
        // Set mode value using the exact backend choices
        if (checked) {
          if (name === "onSite") setMode("On site");
          else if (name === "remote") setMode("Remote");
          else if (name === "hybrid") setMode("Hybrid");
        } else {
          setMode("");
        }
      }
      
      // Handle job type filters (Job, Internship)
      if (["job", "internship"].includes(name)) {
        // Uncheck other type filters
        newFilters.job = name === "job" && checked;
        newFilters.internship = name === "internship" && checked;
        
        // Set type value
        if (checked) {
          if (name === "job") setType("j");
          else if (name === "internship") setType("i");
        } else {
          setType("");
        }
      }
      
      return newFilters;
    });
  };

  // Updated handleApplyFilters function
  // Apply filters and perform search
  const handleApplyFilters = () => {
    // Close the popover
    handleClose();

    // Create a mock event object
    const mockEvent = { preventDefault: () => {} };

    // Call handleSearch with the mock event
    handleSearch(mockEvent);
  };

  // Add form submit handler
  const handleSubmit = (event) => {
    event.preventDefault();
    // Ensure filters are applied before search
    handleApplyFilters();
    handleSearch(event);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        width: "100%",
        mt: 4,
        position: "relative",
      }}
    >
      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <SearchContainer>
          <ClickAwayListener onClickAway={() => setSuggestionAnchorEl(null)}>
            <InputWrapper>
              <img
                src={searchIcon}
                alt={t('searchBox.search.job_placeholder')}
                style={{ marginRight: "8px" }}
              />
              <SearchInput
                ref={searchInputRef}
                placeholder={t('searchBox.search.job_placeholder')}
                fullWidth
                value={searchTerm}
                onChange={handleSearchTermChange}
                sx={{
                  fontFamily: "Poppins",
                  fontSize: 14,
                }}
              />
              <Popper
                open={Boolean(suggestionAnchorEl) && suggestions.titles.length > 0}
                anchorEl={suggestionAnchorEl}
                placement="bottom-start"
                style={{ zIndex: 1300, width: suggestionAnchorEl?.offsetWidth }}
              >
                <Paper elevation={3} sx={{ maxHeight: 300, overflowY: "auto" }}>
                  {suggestions.titles.map((suggestion, index) => (
                    <Typography
                      key={index}
                      onClick={() => handleSuggestionSelect(suggestion, "title")}
                      sx={{
                        p: 1,
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "rgba(0,0,0,0.05)",
                        },
                      }}
                    >
                      {suggestion}
                    </Typography>
                  ))}
                </Paper>
              </Popper>
            </InputWrapper>
          </ClickAwayListener>
          {isMobile || isTablette ? (
            <Divider
              orientation="horizontal"
              sx={{
                height: 0,
                mx: 1.5,
                borderColor: "divider",
                marginTop: 1,
                color: "#00000033",
                border: "1px solid #00000033",
              }}
            />
          ) : (
            <Divider
              orientation="vertical"
              sx={{
                height: 38,
                mx: 1.5,
                borderColor: "divider",
                marginTop: 1,
                color: "#00000033",
                border: "1px solid #00000033",
              }}
            />
          )}
          <ClickAwayListener onClickAway={() => setSuggestionAnchorEl(null)}>
            <InputWrapper isSecondInput>
              <img
                src={locationIcon}
                alt={t('searchBox.search.location_placeholder')}
                style={{ marginRight: "8px" }}
              />
              <SearchInput
                ref={locationInputRef}
                value={location}
                onChange={handleLocationChange}
                placeholder={t('searchBox.search.location_placeholder')}
                fullWidth
                sx={{
                  fontFamily: "Poppins",
                  fontSize: 14,
                }}
              />
              <Popper
                open={Boolean(suggestionAnchorEl) && suggestions.locations.length > 0}
                anchorEl={suggestionAnchorEl}
                placement="bottom-start"
                style={{ zIndex: 1300, width: suggestionAnchorEl?.offsetWidth }}
              >
                <Paper elevation={3} sx={{ maxHeight: 300, overflowY: "auto" }}>
                  {suggestions.locations.map((suggestion, index) => (
                    <Typography
                      key={index}
                      onClick={() => handleSuggestionSelect(suggestion, "localisation")}
                      sx={{
                        p: 1,
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "rgba(0,0,0,0.05)",
                        },
                      }}
                    >
                      {suggestion}
                    </Typography>
                  ))}
                </Paper>
              </Popper>
            </InputWrapper>
          </ClickAwayListener>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
            }}
          >
            <Button
              type="submit"
              variant="contained"
              sx={{
                borderRadius: "11px",
                px: 4,
                backgroundColor: colors.primary,
                textTransform: "none",
                fontFamily: "Poppins",
                fontSize: "14px",
                [theme.breakpoints.down("md")]: {
                  width: "75%",
                  minHeight: "45px",
                  gap: "10px",
                  paddingTop: "16px",
                  paddingBottom: "16px",
                  marginTop: 2,
                },
              }}
            >
              {t('searchBox.search.button')}
            </Button>
            <Button
              variant="outlined"
              onClick={handleFilterClick}
              sx={{
                borderRadius: "11px",
                backgroundColor: "#fff",
                fontFamily: "Poppins",
                minWidth: "auto",
                padding: "8px",
                height: "100%",
                width: "50px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginLeft: 1,
                "& img": {
                  width: "20px",
                  height: "20px",
                },
                [theme.breakpoints.down("md")]: {
                  width: "60px",
                  height: "60px",
                  padding: "8px",
                  marginTop: 2,
                  marginLeft: 2,
                },
              }}
            >
              <img src={filterIcon} alt={t('searchBox.search.button')} />
            </Button>
          </Box>
        </SearchContainer>
      </form>

      {/* Filter Options Popover */}
      <Popover
        open={open}
        anchorEl={filterAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          mt: 1,
        }}
      >
        <FilterContainer>
          {/* <Typography
            variant="subtitle1"
            sx={{ mb: 1, fontFamily: "Poppins", fontWeight: "500" }}
          >
            {t('searchBox.workMode')}
          </Typography> */}
          <FormGroup>
            <StyledFormControlLabel
              control={
                <Checkbox
                  checked={filters.onSite}
                  onChange={handleFilterChange}
                  name="onSite"
                  sx={{
                    color: "#DDDDDD",
                    "&.Mui-checked": {
                      color: "#0000FF",
                    },
                  }}
                />
              }
              label={t('searchBox.onSite')}
            />
            <StyledFormControlLabel
              control={
                <Checkbox
                  checked={filters.remote}
                  onChange={handleFilterChange}
                  name="remote"
                  sx={{
                    color: "#DDDDDD",
                    "&.Mui-checked": {
                      color: "#0000FF",
                    },
                  }}
                />
              }
              label={t('searchBox.remote')}
            />
            <StyledFormControlLabel
              control={
                <Checkbox
                  checked={filters.hybrid}
                  onChange={handleFilterChange}
                  name="hybrid"
                  sx={{
                    color: "#DDDDDD",
                    "&.Mui-checked": {
                      color: "#0000FF",
                    },
                  }}
                />
              }
              label={t('searchBox.hybrid')}
            />

            <Divider sx={{ my: 1 }} />

            {/* <Typography
              variant="subtitle1"
              sx={{ mb: 1, mt: 1, fontFamily: "Poppins", fontWeight: "500" }}
            >
              {t('searchBox.jobType')}
            </Typography> */}

            <StyledFormControlLabel
              control={
                <Checkbox
                  checked={filters.job}
                  onChange={handleFilterChange}
                  name="job"
                  sx={{
                    color: "#DDDDDD",
                    "&.Mui-checked": {
                      color: "#0000FF",
                    },
                  }}
                />
              }
              label={t('searchBox.job')}
            />
            <StyledFormControlLabel
              control={
                <Checkbox
                  checked={filters.internship}
                  onChange={handleFilterChange}
                  name="internship"
                  sx={{
                    color: "#DDDDDD",
                    "&.Mui-checked": {
                      color: "#0000FF",
                    },
                  }}
                />
              }
              label={t('searchBox.internship')}
            />

            {/* <Button
              variant="contained"
              onClick={handleApplyFilters}
              sx={{
                mt: 2,
                backgroundColor: colors.primary,
                textTransform: "none",
                fontFamily: "Poppins",
                borderRadius: "8px",
              }}
            >
              {t('searchBox.applyFilters')}
            </Button> */}
          </FormGroup>
        </FilterContainer>
      </Popover>
    </Box>
  );
}

export default SearchBar;
